import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SESSION_END_CODES, type Permission } from '@pqrs/contracts';
import { Button } from '@xiltrion/ui/button';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Icono } from '@xiltrion/ui/icono';
import { avisar } from '@xiltrion/ui/sonner';
import { apiGet, apiPost, ApiError, onUnauthorized } from '@/api/client';
import type { Me } from '@/api/types';
import { isLeaving, leaveTo, setLeaving } from '@/features/auth/suiteSso';
import { announceSessionEnd, idleSeconds, markActivity, markSessionStarted, onSessionEndElsewhere, startActivityTracking, type SessionEndReason } from './activity';
import { SessionConflictWatcher } from './session-conflict';

/**
 * `can` = permisos generales del rol. Lo que se puede hacer con un expediente o una tarea lo trae el API en `can` de cada uno.
 * `admin` y `supervisor` (lidera un área) deciden qué secciones se ven. `canCreateCase`: tiene `case.create` y ya activó la
 * verificación en dos pasos (sin ella el API rechaza la radicación, así que ni se deja empezar el formulario).
 * `askLogout`: «Cerrar sesión» de un menú o un botón, que primero pregunta; `logout` cierra sin preguntar (desde un
 * diálogo que ya es la decisión, como el del cierre por inactividad). `hasModule`: el módulo de la suite está encendido
 * para la entidad (`me.modules`; `MODULE` de `@pqrs/contracts`); lo apagado no se ofrece.
 */
interface Session { me: Me | null; loading: boolean; can: (p: Permission) => boolean; canCreateCase: boolean; admin: boolean; supervisor: boolean; hasRole: (r: string) => boolean; hasModule: (code: string) => boolean; askLogout: () => void; logout: () => Promise<void>; refresh: () => Promise<unknown> }
const Ctx = createContext<Session>({ me: null, loading: true, can: () => false, canCreateCase: false, admin: false, supervisor: false, hasRole: () => false, hasModule: () => false, askLogout: () => undefined, logout: async () => undefined, refresh: async () => undefined });

/** Aviso antes del cierre por inactividad. */
const IDLE_WARNING_SECONDS = 120;
const END_CODES = new Set<string>(Object.values(SESSION_END_CODES));

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try { return await apiGet<Me>('/auth/me'); } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          if (END_CODES.has(e.code)) announceSessionEnd(e.code as SessionEndReason);
          return null;
        }
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
    // En puesta en marcha se vuelve a preguntar cada minuto: cuando el administrador la termina, la app aparece sola
    // para todos (quien la terminó la ve al instante: `useGoLive` invalida `me`).
    refetchInterval: (query) => (query.state.data?.operation === 'SETUP' ? 60_000 : false),
  });
  const me = q.data ?? null;
  const [confirmLogout, setConfirmLogout] = useState(false);
  // Las pestañas que esperaban otro ingreso (`LoginPage`) se enteran de que ya hay sesión.
  const sessionMark = me?.session?.expiresAt;
  useEffect(() => { if (sessionMark) markSessionStarted(sessionMark); }, [sessionMark]);

  /**
   * La sesión terminó (aquí, en el API o en otra pestaña): se olvida todo y se avisa a las demás pestañas con el motivo.
   * `/login` decide si esta pestaña va a ingresar o espera a otra (`LoginPage`). Saliendo, nada: la pestaña ya va a la suite.
   */
  const end = useCallback((reason: SessionEndReason | null, opts: { tellApi?: boolean; broadcast?: boolean } = {}) => {
    if (isLeaving()) return;
    if (opts.tellApi) void apiPost('/auth/logout').catch(() => undefined);
    if (reason && opts.broadcast !== false) announceSessionEnd(reason);
    qc.setQueryData(['me'], null);
    qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
  }, [qc]);

  useEffect(() => startActivityTracking(), []);
  useEffect(() => { onUnauthorized((code) => end(END_CODES.has(code) ? (code as SessionEndReason) : 'SESSION_REVOKED')); }, [end]);
  useEffect(() => onSessionEndElsewhere(() => end(null, { broadcast: false })), [end]);

  const value: Session = {
    me,
    loading: q.isLoading,
    can: (p) => !!me?.permissions.includes(p),
    canCreateCase: !!me?.permissions.includes('case.create'),
    admin: !!me?.permissions.includes('admin.manage'),
    supervisor: !!me?.supervisor,
    hasRole: (r) => !!me?.roleCodes.includes(r),
    hasModule: (code) => !!me?.modules.includes(code),
    askLogout: () => setConfirmLogout(true),
    // «Salir» (GUIA §3.1): desde aquí ningún 401 manda a ingresar; el API cierra la sesión de SGDEA y devuelve siempre el
    // cierre del SSO en la suite, que cierra también las demás apps (por back-channel: no se avisa a las otras pestañas)
    // y aterriza en su portada con la entidad. Si el API no responde, se avisa y el diálogo queda abierto: nunca a
    // `/login` ni al ingreso de la suite, que con el SSO vivo volvería a entrar sola.
    logout: async () => {
      setLeaving(true);
      try {
        const r = await apiPost<{ endSessionUrl: string | null }>('/auth/logout');
        if (!r?.endSessionUrl) throw new Error('El API no devolvió a dónde ir al salir');
        leaveTo(r.endSessionUrl);
      } catch (e) {
        setLeaving(false);
        avisar.error('No se pudo cerrar la sesión. Intenta de nuevo.');
        throw e;
      }
    },
    refresh: () => q.refetch(),
  };
  return (
    <Ctx.Provider value={value}>
      {children}
      {/* El mismo texto en todas las apps de la suite (GUIA §3.1, «Salir»). */}
      <ConfirmarDialogo
        abierto={confirmLogout}
        onAbiertoChange={setConfirmLogout}
        titulo="¿Está seguro que desea cerrar su sesión?"
        descripcion="Se cerrará en todas las apps de la suite Datalog."
        textoConfirmar="Cerrar sesión"
        tamano="sm"
        tono="warning"
        icono="mdi:logout"
        onConfirmar={value.logout}
      />
      {/* «Continuar en la otra sesión» sale también del SSO de la suite en este navegador, como «Salir». */}
      {me && <SessionConflictWatcher onEnd={(reason, endSessionUrl) => { if (endSessionUrl) leaveTo(endSessionUrl); end(reason); }} />}
      {me?.session && <SessionTimer session={me.session} onEnd={(reason) => end(reason, { tellApi: true })} onLogout={value.logout} onStay={() => { markActivity(); void q.refetch(); }} />}
    </Ctx.Provider>
  );
}
export const useSession = () => useContext(Ctx);

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.max(0, s % 60)).padStart(2, '0')}`;

/**
 * Relojes de la sesión: a los `idleTimeoutSeconds` sin usar la pantalla la cierra (avisa dos minutos antes, con
 * «Seguir conectado»), y al llegar a su vigencia máxima también (avisa cinco minutos antes; esa no se puede extender).
 */
function SessionTimer({ session, onEnd, onLogout, onStay }: { session: NonNullable<Me['session']>; onEnd: (reason: SessionEndReason) => void; onLogout: () => Promise<void>; onStay: () => void }) {
  const [now, setNow] = useState(() => Date.now());
  const ended = useRef(false);
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  const idleLeft = session.idleTimeoutSeconds - idleSeconds();
  const absoluteLeft = Math.floor((new Date(session.expiresAt).getTime() - now) / 1000);
  useEffect(() => {
    if (ended.current) return;
    if (absoluteLeft <= 0) { ended.current = true; onEnd('SESSION_EXPIRED'); }
    else if (idleLeft <= 0) { ended.current = true; onEnd('SESSION_IDLE'); }
  }, [absoluteLeft, idleLeft, onEnd]);
  const [dismissedAbsolute, setDismissedAbsolute] = useState(false);
  const idleWarning = idleLeft > 0 && idleLeft <= IDLE_WARNING_SECONDS;
  const absoluteWarning = !idleWarning && !dismissedAbsolute && absoluteLeft > 0 && absoluteLeft <= 300;
  if (idleWarning) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) onStay(); }}>
        <DialogContent className="sm:max-w-[420px]" aria-describedby="session-idle-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Icono nombre="mdi:timer-sand" aria-hidden /> ¿Sigues ahí?</DialogTitle>
            <DialogDescription id="session-idle-desc">
              Por seguridad, tu sesión se cerrará por inactividad en <strong className="tnum" aria-live="polite">{mmss(idleLeft)}</strong>. Lo que no hayas guardado se perderá.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => void onLogout().catch(() => undefined)}>Cerrar sesión</Button>
            <Button onClick={onStay} autoFocus><Icono nombre="mdi:check" /> Seguir conectado</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  if (absoluteWarning) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) setDismissedAbsolute(true); }}>
        <DialogContent className="sm:max-w-[420px]" aria-describedby="session-expiry-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Icono nombre="mdi:clock-alert-outline" aria-hidden /> Tu sesión está por vencer</DialogTitle>
            <DialogDescription id="session-expiry-desc">
              Las sesiones duran una jornada. La tuya vence en <strong className="tnum">{mmss(absoluteLeft)}</strong>: guarda lo que estés haciendo y vuelve a ingresar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter><Button onClick={() => setDismissedAbsolute(true)}>Entendido</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return null;
}
